/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/firstUseNotifier.scss';

import {CloseButton, voidFunction, WithBackground} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {useContext, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {AppContext} from '../misc/appContext';

import {BolmateButton} from '../controls';

import {hasAdminLogin} from '../../util/adminUtil';

const notifyKey = 'vat_decl_first_notified';

export default function FirstUseNotifier() {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const [open, setOpen] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(10);
    const timeout = useRef<number | null>(null);
    const counter = useRef<number | null>(null);
    const currentCount = useRef<number>(10);
    const userIsAdmin = hasAdminLogin() || (context.userContext.user?.isAdmin() ?? false);

    function onConfirm() {
        const {user} = context.userContext;
        if (!user) return setOpen(false);
        user.setFlex(notifyKey, true);
        user.update(undefined, () => {
            setOpen(false);
        });
    }

    function onClose() {
        setOpen(false);
    }

    function notifyFirstUse() {
        const {user} = context.userContext;
        if (!user) return;
        if (user.getFlex(notifyKey) === true) return;
        setOpen(true);
        window.setTimeout(() => {
            setLoading(true);
        });
    }

    function clearTimeout() {
        if (timeout.current !== null) {
            window.clearTimeout(timeout.current);
            timeout.current = null;
        }
        if (counter.current !== null) {
            window.clearTimeout(counter.current);
            counter.current = null;
        }
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <p>
                    {t(
                        'vatDeclaration.firstUse.intro1',
                        'De BTW aangifte tool helpt bij het berekenen van de BTW bedragen van jouw Bol verkopen.'
                    )}
                    <br />
                    {t(
                        'vatDeclaration.firstUse.intro2',
                        'Je kan deze cijfers inzien over een bepaalde periode (bijvoorbeeld over een kwartaal of jaar), per land én per omzetgroep.'
                    )}
                    <br />
                    {t('vatDeclaration.firstUse.intro3', 'Handig als hulp voor bij jouw BTW aangiftes!')}
                    <br />
                    <br />
                    {t(
                        'vatDeclaration.firstUse.calc1',
                        'Wij maken deze berekening door de Excel specificaties van de verkoopfacturen van jouw Bol account te downloaden en te verwerken.'
                    )}{' '}
                    <br />
                    {t(
                        'vatDeclaration.firstUse.calc2',
                        'In deze specificaties ontbreekt het BTW percentage van jouw producten, daarom nemen wij het in Bolmate ingestelde BTW percentages van jouw producten over.'
                    )}
                    <br />
                    {t(
                        'vatDeclaration.firstUse.calc3',
                        'Zorg dus dat de BTW percentages van producten correct in Bolmate zijn ingesteld.'
                    )}
                    <br />
                    {t(
                        'vatDeclaration.firstUse.calc4',
                        'Indien aanwezig, nemen wij ook de via Bolmate handmatig verlegde BTW van facturen over.'
                    )}
                    <br />
                    {t(
                        'vatDeclaration.firstUse.calc5',
                        'Vervolgens tellen wij de bedragen bij elkaar op tot de totalen, en geven deze per categorie weer.'
                    )}
                    <br />
                    <br />
                    <b>{t('vatDeclaration.firstUse.attention', 'Let op het volgende:')}</b>
                    <br />
                    <br />
                    {t('vatDeclaration.firstUse.bullet1', '- Deze tool is enkel bedoeld als een hulpmiddel.')}
                    <br />
                    {t(
                        'vatDeclaration.firstUse.bullet2',
                        '- Wij kunnen geen garanties geven over de compleetheid en correctheid.'
                    )}
                    <br />
                    {t('vatDeclaration.firstUse.bullet3', '- Deze tool dient niet ter vervanging van een boekhouder.')}
                    <br />
                    {t(
                        'vatDeclaration.firstUse.bullet4',
                        '- Wij kunnen verder geen ondersteuning bieden bij het opmaken van belastingaangiftes.'
                    )}
                </p>
            </div>
        );
    }

    function renderFooter() {
        let buttonLabel = t('vatDeclaration.firstUse.confirm', 'Ja, ik begrijp het');
        if (disabled) buttonLabel += ` (${count})`;
        return (
            <div className='footer-container'>
                <BolmateButton onClick={onConfirm} label={buttonLabel} disabled={disabled} className='confirm-btn' />
                <div className={classNames('first-use-loader', loading && 'active')}>
                    <div className='loader' />
                </div>
            </div>
        );
    }

    function decreaseCount() {
        const nextCount = currentCount.current - 1;
        currentCount.current = nextCount;
        setCount(nextCount);
        if (nextCount > 0) counter.current = window.setTimeout(decreaseCount, 1000);
    }

    useEffect(() => {
        notifyFirstUse();
        return function () {
            clearTimeout();
        };
    }, []);

    useEffect(() => {
        if (open) {
            timeout.current = window.setTimeout(() => {
                setDisabled(false);
            }, 10000);
            counter.current = window.setTimeout(() => {
                decreaseCount();
            }, 1000);
        } else clearTimeout();
        return function () {
            clearTimeout();
        };
    }, [open]);

    return (
        <WithBackground
            active={open}
            onClose={userIsAdmin ? onClose : voidFunction}
            escapeDisabled={!userIsAdmin}
            preventBackgroundClick={!userIsAdmin}
        >
            <div className='vat-decl-first-use default-popup'>
                <div className='header-container'>
                    <label className='header-title'>
                        {t('vatDeclaration.firstUse.title', 'Welkom bij de BTW aangifte hulp!')}
                    </label>
                    {userIsAdmin && <CloseButton onClick={onClose} />}
                </div>
                {renderBody()}
                {renderFooter()}
            </div>
        </WithBackground>
    );
}
